import type { TextEngineService } from '../../text-engine';
import type { FontLibrary } from '../library';
import type { FontFallback, RegisteredFallback } from './types';
import { fetchFallbackFontBytes } from './utils';

/**
 * 创建字体资源的注册键。
 *
 * 该键只描述“哪一份字体字节”：字体 id 区分业务素材，src 区分同一素材的不同资源版本。
 * `inFlight`、`registrations` 和文本引擎注册都复用此键，因此同一资源只会下载并注册一次。
 * common/script 等 fallback 链策略不属于资源身份，由 {@link createFallbackSpecKey} 单独表达。
 *
 * @param id - 业务字体目录中的字体 id
 * @param src - 字体资源 URL
 * @returns 可用于并发去重和文本引擎注册的稳定键
 */
const createFallbackRegistrationKey = (id: string, src: string) => `fallback-${id}-${encodeURIComponent(src)}`;

/**
 * 创建一条 fallback 链配置的终态键。
 *
 * 同一份字体资源可以同时承担 common fallback 和多个 script fallback；它们共享
 * registrationKey，但必须分别写入对应链、分别记录成功或失败终态。因此该键在资源身份后
 * 继续编码 role 和 scripts，仅用于 `settledSpecKeys`，不会传给文本引擎。
 * `\u0000` 用作字段分隔符，避免普通字体名、URL 或 script tag 拼接后产生边界歧义。
 *
 * @param registrationKey - 字体资源注册键
 * @param fallback - 需要应用的 common/script fallback 配置
 * @returns 可唯一标识本次链配置的终态键
 */
const createFallbackSpecKey = (registrationKey: string, fallback: FontFallback) =>
    `${registrationKey}\u0000${fallback.role}\u0000${fallback.role === 'script' ? fallback.scripts.join('\u0000') : ''}`;

export class FontFallbackManager {
    /**
     * 正在处理中的 fallback 请求，键为请求的 registrationKey。
     */
    private inFlight = new Map<string, Promise<RegisteredFallback>>();

    /**
     * 已注册的 fallback 字体，键为 registrationKey。
     */
    private registrations = new Map<string, RegisteredFallback>();

    /**
     * 已处理的 fallback 字体链配置键集合。
     */
    private settledSpecKeys = new Set<string>();

    private commonFamilies = new Set<string>();
    private scriptFamilies = new Map<string, Set<string>>();

    constructor(
        private readonly textEngine: TextEngineService,
        private readonly library: FontLibrary,
    ) {}

    /**
     * 请求一批 fallback；单项失败不影响其余项。
     * @param fallbacks - 待请求的 fallback 字体配置
     */
    public async request(fallbacks: FontFallback[]): Promise<void> {
        await Promise.allSettled(fallbacks.map((fallback) => this.ensure(fallback)));
    }

    /**
     * 判断字体族是否已经注册到文本引擎。
     * @param fontFamily - 字体族名称
     * @returns 是否已注册
     */
    public hasRegistered(fontFamily: string) {
        for (const registration of this.registrations.values()) {
            if (registration.fontFamily === fontFamily || registration.engineFamilyName === fontFamily) {
                return true;
            }
        }
        return false;
    }

    private async ensure(fallback: FontFallback): Promise<void> {
        const src = this.library.getById(fallback.id)?.ttf || '';
        const registrationKey = createFallbackRegistrationKey(fallback.id, src);
        const specKey = createFallbackSpecKey(registrationKey, fallback);
        if (this.settledSpecKeys.has(specKey)) return;

        if (!src) {
            this.settledSpecKeys.add(specKey);
            return;
        }

        try {
            const registration = await this.getOrCreate(fallback, src, registrationKey);
            this.setChains(fallback, registration.engineFamilyName);
        } finally {
            this.settledSpecKeys.add(specKey);
        }
    }

    private getOrCreate(fallback: FontFallback, src: string, registrationKey: string) {
        const registered = this.registrations.get(registrationKey);
        if (registered) return Promise.resolve(registered);

        let task = this.inFlight.get(registrationKey);
        if (!task) {
            task = this.createRegistration(fallback, src, registrationKey);
            this.inFlight.set(registrationKey, task);
        }
        return task;
    }

    private async createRegistration(fallback: FontFallback, src: string, registrationKey: string) {
        try {
            const bytes = await fetchFallbackFontBytes(fallback, src);
            const registration = {
                fontFamily: fallback.fontFamily,
                engineFamilyName: this.register(bytes, registrationKey),
            };
            this.registrations.set(registrationKey, registration);
            return registration;
        } finally {
            this.inFlight.delete(registrationKey);
        }
    }

    private register(bytes: Uint8Array, registrationKey: string): string {
        const engine = this.textEngine.getEngine();
        if (!engine || engine.isDisposed) throw new Error('Text engine is not available.');

        const result = engine.registerFont(registrationKey, bytes);
        if (!result.ok) throw result.error;

        const family = engine.getFontFamilyName(registrationKey);
        if (!family) throw new Error('Text engine did not return a fallback family name.');
        this.textEngine.markFontStateChanged();
        return family;
    }

    private setChains(fallback: FontFallback, engineFamilyName: string) {
        const engine = this.textEngine.getEngine();
        if (!engine || engine.isDisposed) throw new Error('Text engine is not available.');

        if (fallback.role === 'common') {
            if (this.commonFamilies.has(engineFamilyName)) return;

            const result = engine.setCommonFallback([...this.commonFamilies, engineFamilyName]);
            if (!result.ok) throw result.error;

            this.commonFamilies.add(engineFamilyName);
            this.textEngine.markFontStateChanged();
            return;
        }

        for (const script of fallback.scripts) {
            const families = this.scriptFamilies.get(script) || new Set<string>();
            if (families.has(engineFamilyName)) continue;

            const result = engine.setScriptFallback(script, [...families, engineFamilyName]);
            if (!result.ok) throw result.error;

            families.add(engineFamilyName);
            this.textEngine.markFontStateChanged();
            this.scriptFamilies.set(script, families);
        }
    }

    public dispose() {
        this.inFlight.clear();
        this.registrations.clear();
        this.settledSpecKeys.clear();
        this.commonFamilies.clear();
        this.scriptFamilies.clear();
    }
}
